import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { recoilLoginState } from '../../RecoilStore';
import { useDropzone } from 'react-dropzone';
import imageCompression from "browser-image-compression";
import { format } from "date-fns";
import Loading from '../../components/Loading';
import { Icon, PageHead } from '../AdminLayout';

interface PopupItem {
  id?: number;
  title: string;
  image: string;
  content: string;
  link: string;
  startDate: string;
  endDate: string;
  isActive: number;
}

const emptyPopup: PopupItem = { title: '', image: '', content: '', link: '', startDate: '', endDate: '', isActive: 1 };

export default function EditPopup() {
  const navigate = useNavigate();
  const isLogin = useRecoilValue(recoilLoginState);

  const [popups, setPopups] = useState<PopupItem[]>([]);
  const [editing, setEditing] = useState<PopupItem | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(false);

  const date = format(new Date(), 'yyyy-MM-dd');

  // 임의의 영문+숫자 문자열 생성 함수
  const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const fetchPopups = async () => {
    try {
      const res = await axios.get(`${MainURL}/main/getpopups/all`);
      setPopups(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('팝업 로드 오류:', e);
      setPopups([]);
    }
  };

  useEffect(() => {
    if (!isLogin) {
      alert('로그인이 필요합니다.');
      navigate('/admin');
      return;
    }
    fetchPopups();
  }, []);

  // 이미지 선택 시 압축 후 바로 업로드
  const popupDropzone = useDropzone({
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        alert('jpg, jpeg, png 형식의 이미지 파일만 업로드할 수 있습니다.');
        return;
      }
      setImageLoading(true);
      try {
        const resized = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1200 });
        const extension = file.type.includes('png') ? '.png' : '.jpg';
        const renamed = new File([resized], `${date}admin_popup_${generateRandomString(10)}${extension}`, { type: file.type });
        const fd = new FormData();
        fd.append('img', renamed);
        const res = await axios.post(`${MainURL}/main/upload/popup`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data && res.data.filename) {
          setEditing(prev => prev ? { ...prev, image: res.data.filename } : prev);
        } else {
          alert('이미지 업로드에 실패했습니다.');
        }
      } catch (error: any) {
        console.error('팝업 이미지 업로드 오류:', error);
        alert(error.response?.data?.message || '이미지 업로드 중 오류가 발생했습니다.');
      } finally {
        setImageLoading(false);
      }
    }, []),
    multiple: false
  });

  const save = async () => {
    if (!editing) return;
    if (!editing.image && !editing.title && !editing.content) {
      alert('이미지, 제목, 내용 중 하나는 입력해야 합니다.');
      return;
    }
    if (editing.startDate && editing.endDate && editing.startDate > editing.endDate) {
      alert('게시 종료일이 시작일보다 빠릅니다.');
      return;
    }
    try {
      const res = await axios.post(`${MainURL}/main/popup/save`, editing);
      if (res.data && res.data.success) {
        alert('저장되었습니다.');
        setEditing(null);
        fetchPopups();
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch (e) {
      console.error('팝업 저장 오류:', e);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const remove = async (item: PopupItem) => {
    if (!window.confirm(`<${item.title || '팝업'}> 팝업을 삭제하시겠습니까?`)) return;
    try {
      const res = await axios.post(`${MainURL}/main/popup/delete`, { id: item.id });
      if (res.data && res.data.success) {
        fetchPopups();
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (e) {
      console.error('팝업 삭제 오류:', e);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const toggleActive = async (item: PopupItem) => {
    try {
      const res = await axios.post(`${MainURL}/main/popup/save`, { ...item, isActive: item.isActive ? 0 : 1 });
      if (res.data && res.data.success) fetchPopups();
    } catch (e) {
      console.error('팝업 상태 변경 오류:', e);
    }
  };

  const periodText = (item: PopupItem) => {
    if (!item.startDate && !item.endDate) return '기간 제한 없음';
    return `${item.startDate || '제한 없음'} ~ ${item.endDate || '제한 없음'}`;
  };

  return (
    <>
      <PageHead title="팝업 관리" desc="'사용'으로 설정되고 게시 기간 안에 있는 팝업이 홈페이지 첫 화면에 표시됩니다.">
        {editing === null && (
          <button type="button" className="ad-btn ad-btn--primary" onClick={() => setEditing({ ...emptyPopup })}>
            <Icon name="plus" />
            팝업 추가
          </button>
        )}
      </PageHead>

      {editing === null && (
        <div className="ad-card">
          <div className="ad-card__head">
            <h2>등록된 팝업</h2>
            <p>전체 {popups.length}개 · 사용 중 {popups.filter((p) => p.isActive).length}개</p>
          </div>
          <div className="ad-card__pad">
            {popups.length === 0 ? (
              <div className="ad-empty">
                <Icon name="popup" />
                <p>등록된 팝업이 없습니다.</p>
                <button type="button" className="ad-btn ad-btn--primary ad-btn--sm" onClick={() => setEditing({ ...emptyPopup })}>팝업 추가하기</button>
              </div>
            ) : (
              <div className="ad-items">
                {popups.map((item) => (
                  <div key={item.id} className="ad-item">
                    <div className="ad-item__row">
                      {item.image
                        ? <img className="ad-thumb" src={`${MainURL}/images/popup/${item.image}`} alt="" />
                        : <span className="ad-thumb ad-thumb--empty"><Icon name="image" /></span>}
                      <div className="ad-item__body">
                        <h3>{item.title || '제목 없음'}</h3>
                        <p>{periodText(item)}</p>
                        <span className={`ad-badge ${item.isActive ? 'ad-badge--on' : ''}`} style={{ marginTop: 6 }}>
                          <i></i>{item.isActive ? '사용' : '사용 안 함'}
                        </span>
                      </div>
                      <div className="ad-item__acts">
                        <button type="button" className="ad-btn ad-btn--sm" onClick={() => toggleActive(item)}>
                          {item.isActive ? '사용 중지' : '사용'}
                        </button>
                        <button type="button" className="ad-btn ad-btn--sm" onClick={() => setEditing({ ...item })}>
                          <Icon name="edit" />수정
                        </button>
                        <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger" onClick={() => remove(item)}>
                          <Icon name="trash" />삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {editing !== null && (
        <div className="ad-card">
          <div className="ad-card__head">
            <h2>{editing.id ? '팝업 수정' : '팝업 추가'}</h2>
            <p>이미지, 제목, 내용 중 하나는 입력해야 합니다.</p>
          </div>

          <div className="ad-card__pad">
            <div className="ad-field">
              <label>이미지 <em>선택</em></label>
              {editing.image && (
                <img className="ad-thumb ad-thumb--md" style={{ height: 'auto', maxWidth: 280, width: '100%' }}
                  src={`${MainURL}/images/popup/${editing.image}`} alt="" />
              )}
              {imageLoading ? (
                <div className="ad-loading"><Loading/></div>
              ) : (
                <div className="ad-upload">
                  <div {...popupDropzone.getRootProps()}>
                    <input {...popupDropzone.getInputProps()} />
                    <span className="ad-drop"><Icon name="image" />{editing.image ? '다시 첨부하기' : '이미지 첨부하기'}</span>
                  </div>
                  {editing.image && (
                    <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger" onClick={() => setEditing({ ...editing, image: '' })}>
                      이미지 빼기
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="ad-field">
              <label htmlFor="popupTitle">제목 <em>선택</em></label>
              <input id="popupTitle" className="ad-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </div>

            <div className="ad-field">
              <label htmlFor="popupContent">내용 <em>선택</em></label>
              <textarea id="popupContent" className="ad-input" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
            </div>

            <div className="ad-field">
              <label htmlFor="popupLink">클릭 시 이동할 링크 <em>선택</em></label>
              <input id="popupLink" className="ad-input" placeholder="예: /notice 또는 https://..." value={editing.link} onChange={(e) => setEditing({ ...editing, link: e.target.value })} />
            </div>

            <div className="ad-field">
              <label>게시 기간</label>
              <div className="ad-inline" style={{ maxWidth: 420 }}>
                <input type="date" className="ad-input" value={editing.startDate || ''} onChange={(e) => setEditing({ ...editing, startDate: e.target.value })} />
                <span>~</span>
                <input type="date" className="ad-input" value={editing.endDate || ''} onChange={(e) => setEditing({ ...editing, endDate: e.target.value })} />
              </div>
              <p className="ad-hint">비워두면 기간 제한 없이 표시됩니다.</p>
            </div>

            <div className="ad-field">
              <label className="ad-check">
                <input type="checkbox" checked={!!editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked ? 1 : 0 })} />
                홈페이지에 표시
              </label>
            </div>
          </div>

          <div className="ad-card__foot">
            <span className="grow">저장하면 홈페이지에 바로 반영됩니다.</span>
            <button type="button" className="ad-btn" onClick={() => setEditing(null)}>취소</button>
            <button type="button" className="ad-btn ad-btn--primary" onClick={save}>저장</button>
          </div>
        </div>
      )}
    </>
  );
}
